/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback, useRef } from "react";
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import { getGridApi, setGridInitApi, setGridSaveApi } from 'service/common';
import Restful from "service/restful";

import alertMsg from 'components/message';

/**
 * 컬럼 정의 복제 (width·순서 등 초기값 유지)
 */
const cloneColumnDefs = (columns) => {
  if (!columns || !Array.isArray(columns)) {
    return columns;
  }

  return columns.map((col) => ({ ...col }));
};

const getColId = (col) => col?.colId || col?.field;

const getColumnKey = (column) => {
  const colDef = column?.getColDef?.() ?? column?.colDef ?? column ?? {};

  return colDef.field || column?.colId || getColId(colDef);
};

const matchSavedColumn = (colDef, savedItem) => {
  const id = savedItem?.field;
  if (!id) return false;

  return colDef.field === id || getColId(colDef) === id;
};

/**
 * 초기 컬럼 순서·width 로 AG Grid column state 생성
 */
const buildColumnStateFromDefs = (columns) => {
  return columns
    .filter((col) => getColId(col))
    .map((col) => {
      const state = { colId: getColId(col) };

      if (col.width != null) {
        state.width = col.width;
      }

      if (col.flex != null) {
        state.flex = col.flex;
      }

      if (col.hide != null) {
        state.hide = col.hide;
      }

      if (col.pinned != null) {
        state.pinned = col.pinned;
      }

      return state;
    });
};

/**
 * DB 저장 JSON → AG Grid column state (실제 colId 매칭)
 */
const buildColumnStateFromSaved = (savedColumns, columnModel) => {
  if (!savedColumns || !Array.isArray(savedColumns)) {
    return [];
  }

  const displayed = columnModel?.getAllDisplayedColumns?.() ?? [];

  return savedColumns
    .filter((item) => item?.field)
    .map((item) => {
      const matched = displayed.find((col) => getColumnKey(col) === item.field || col.colId === item.field);

      return {
        colId: matched ? matched.colId : item.field,
        width: item.actualWidth,
      };
    });
};

const useGridConfig = (initialValues, setColumnDefs, defaultColumnDefs, isMessage = true) => {

  const [getApi] = Restful();

  const [gridApi, setGridApi] = useState({});
  const [gridRemountKey, setGridRemountKey] = useState(0);
  const gridApiRef = useRef(null);
  const columnApiRef = useRef(null);
  const initialColumnDefsRef = useRef(null);
  const skipGetGridRef = useRef(false);
  const pendingResetApplyRef = useRef(false);
  const pendingResetColumnsRef = useRef(null);
  const [gridNo] = useState(initialValues);
  const currentPage = useSelector(state => state.menuList?.current);
  const prevPage = useRef(currentPage);

  let defaultColumns = defaultColumnDefs;
  let showMessage = isMessage;

  if (typeof defaultColumnDefs === 'boolean') {
    showMessage = defaultColumnDefs;
    defaultColumns = null;
  }

  /** 페이지 최초 컬럼 정의 스냅샷 (그리드 초기화 전용) */
  const defaultColumnsSnapshotRef = useRef(
    defaultColumns && Array.isArray(defaultColumns)
      ? cloneColumnDefs(defaultColumns)
      : null
  );

  /**
   * 초기화에 사용할 컬럼 정의 (코드에 정의된 순서·width)
   */
  const resolveResetColumnDefs = useCallback((gridInfo) => {
    if (gridInfo && Array.isArray(gridInfo)) {
      return cloneColumnDefs(gridInfo);
    }

    if (defaultColumnsSnapshotRef.current?.length) {
      return cloneColumnDefs(defaultColumnsSnapshotRef.current);
    }

    if (defaultColumns && Array.isArray(defaultColumns)) {
      return cloneColumnDefs(defaultColumns);
    }

    if (initialColumnDefsRef.current?.length) {
      return cloneColumnDefs(initialColumnDefsRef.current);
    }

    return null;
  }, [defaultColumns]);

  /**
   * onGridReady event / columnApi / gridApi 에서 API 추출
   */
  const registerGridApi = useCallback((input) => {
    let columnApi = input;
    let api = null;

    if (input?.api) {
      api = input.api;
      columnApi = input.columnApi ?? input.api;
    } else if (input?.columnModel) {
      columnApi = input;
      api = input.columnModel?.gridApi
        ?? input.gridOptionsWrapper?.gridOptions?.api
        ?? null;
    } else if (input?.setGridOption) {
      api = input;
      columnApi = input;
    }

    setGridApi(columnApi ?? {});
    columnApiRef.current = columnApi;
    gridApiRef.current = api;

    if (
      !defaultColumnsSnapshotRef.current &&
      !initialColumnDefsRef.current &&
      columnApi?.columnModel?.columnDefs?.length
    ) {
      initialColumnDefsRef.current = cloneColumnDefs(columnApi.columnModel.columnDefs);
    }

    if (pendingResetApplyRef.current && pendingResetColumnsRef.current?.length && columnApi) {
      pendingResetApplyRef.current = false;
      const resetCols = pendingResetColumnsRef.current;
      pendingResetColumnsRef.current = null;

      if (api?.setColumnDefs) {
        api.setColumnDefs(resetCols);
      }

      if (typeof columnApi.applyColumnState === 'function') {
        columnApi.applyColumnState({
          state: buildColumnStateFromDefs(resetCols),
          applyOrder: true,
        });
      }

      api?.refreshHeader?.();
    }
  }, []);

  /**
   * DB 저장 설정 반영 (순서·너비)
   */
  const applySavedGridConfig = useCallback((savedColumns) => {
    const columnApi = columnApiRef.current;
    const columnModel = gridApiRef.current?.columnModel ?? columnApi?.columnModel ?? gridApi?.columnModel;

    if (!columnModel?.columnDefs?.length || !savedColumns?.length) {
      return;
    }

    const baseDefs = cloneColumnDefs(
      defaultColumnsSnapshotRef.current
        ?? initialColumnDefsRef.current
        ?? columnModel.columnDefs
    );
    const newColumns = [];
    const otherColumns = [];

    savedColumns.forEach((item) => {
      const matched = baseDefs.filter((subItem) => matchSavedColumn(subItem, item));

      if (matched.length > 0) {
        newColumns.push({ ...matched[0], width: item.actualWidth });
      }
    });

    baseDefs.forEach((item) => {
      const exists = savedColumns.some((subItem) => matchSavedColumn(item, subItem));

      if (!exists) {
        otherColumns.push({ ...item });
      }
    });

    const merged = newColumns.concat(otherColumns);
    const cloned = cloneColumnDefs(merged);
    const columnState = buildColumnStateFromSaved(savedColumns, columnModel);

    flushSync(() => {
      if (setColumnDefs instanceof Function) {
        setColumnDefs(cloned);
      } else if (setColumnDefs?.current !== undefined) {
        setColumnDefs.current = cloned;
      }
    });

    const applyState = () => {
      if (typeof columnApi?.applyColumnState === 'function' && columnState.length > 0) {
        columnApi.applyColumnState({
          state: columnState,
          applyOrder: true,
        });
      }

      gridApiRef.current?.refreshHeader?.();
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(applyState);
    } else {
      applyState();
    }
  }, [setColumnDefs, gridApi]);

  /**
   * 저장 직후 — 현재 화면 그대로 유지·React state 동기화
   */
  const applySavedGridConfigAfterSave = useCallback((savedColumns) => {
    const columnApi = columnApiRef.current;
    const columnModel = gridApiRef.current?.columnModel ?? columnApi?.columnModel ?? gridApi?.columnModel;

    if (!columnModel?.getAllDisplayedColumns || !savedColumns?.length) {
      return;
    }

    const columnState = buildColumnStateFromSaved(savedColumns, columnModel);
    const baseDefs = cloneColumnDefs(
      defaultColumnsSnapshotRef.current
        ?? initialColumnDefsRef.current
        ?? columnModel.columnDefs
    );
    const newColumns = [];
    const otherColumns = [];

    savedColumns.forEach((item) => {
      const matched = baseDefs.filter((subItem) => matchSavedColumn(subItem, item));

      if (matched.length > 0) {
        newColumns.push({ ...matched[0], width: item.actualWidth });
      }
    });

    baseDefs.forEach((item) => {
      const exists = savedColumns.some((subItem) => matchSavedColumn(item, subItem));

      if (!exists) {
        otherColumns.push({ ...item });
      }
    });

    const cloned = cloneColumnDefs(newColumns.concat(otherColumns));

    flushSync(() => {
      if (setColumnDefs instanceof Function) {
        setColumnDefs(cloned);
      } else if (setColumnDefs?.current !== undefined) {
        setColumnDefs.current = cloned;
      }
    });

    const applyState = () => {
      if (typeof columnApi?.applyColumnState === 'function' && columnState.length > 0) {
        columnApi.applyColumnState({
          state: columnState,
          applyOrder: true,
        });
      }

      gridApiRef.current?.refreshHeader?.();
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(applyState);
    } else {
      applyState();
    }
  }, [setColumnDefs, gridApi]);

  /**
   * 그리드 화면 갱신 — AgGridReact key 변경으로 완전 리마운트 (초기화 전용)
   */
  const remountGrid = useCallback((resetColumns) => {
    pendingResetApplyRef.current = true;
    pendingResetColumnsRef.current = resetColumns;
    columnApiRef.current = null;
    gridApiRef.current = null;
    setGridApi({});
    setGridRemountKey((key) => key + 1);
  }, []);

  /***************************************************************************************
   * 설명 : 그리드 설정 불러오기
  ***************************************************************************************/
  const getGrid = useCallback((page) => {
    if (skipGetGridRef.current) {
      skipGetGridRef.current = false;
      return;
    }

    let params = {
      menuSeq: page?.seq,
      gridNo: gridNo
    }

    getGridApi(getApi, params, (response) => {
      if (
        response !== undefined &&
        response.data?.result &&
        response.data?.data &&
        response.data.data.gridJson
      ) {
        const columns = JSON.parse(response.data.data.gridJson);

        if (columns && Array.isArray(columns)) {
          applySavedGridConfig(columns);
        }
      }
    });
  }, [getApi, gridNo, applySavedGridConfig]);

  /***************************************************************************************
   * 설명 : 그리드 설정저장
  ***************************************************************************************/
  const setGridUpdate = () => {
    const columnApi = columnApiRef.current ?? gridApi;

    if (!columnApi || Object.keys(columnApi).length < 1) {
      alertMsg("그리드 설정이 되어 있지 않습니다.", "E", 3000);
      return;
    };

    const columnModel = gridApiRef.current?.columnModel ?? columnApi?.columnModel;

    if (!columnModel?.getAllDisplayedColumns) {
      alertMsg("그리드 설정이 되어 있지 않습니다.", "E", 3000);
      return;
    }

    const data = columnModel.getAllDisplayedColumns().map((column) => ({
      field: getColumnKey(column),
      actualWidth: column.actualWidth,
    }));

    let params = {
      menuSeq: currentPage.seq,
      gridNo: gridNo,
      gridJson: JSON.stringify(data),
    }

    setGridSaveApi(getApi, params, (response) => {
      if (response.data.result) {
        skipGetGridRef.current = true;
        applySavedGridConfigAfterSave(data);

        if (showMessage) {
          alertMsg(response.data.message, "S", 3000);
        }
      } else {
        if (showMessage) {
          alertMsg(response.data.message, 'E', 3000);
        }
      }
    });
  }

  /***************************************************************************************
   * 설명 : 그리드 설정 초기화 — 초기 width·컬럼 순서로 복원
  ***************************************************************************************/
  const setGridInit = useCallback((gridInfo) => {
    const columnApi = columnApiRef.current ?? gridApi;

    if (!columnApi || Object.keys(columnApi).length < 1) {
      alertMsg("그리드 설정이 되어 있지 않습니다.", "E", 3000);
      return;
    };

    let params = {
      menuSeq: currentPage?.seq,
      gridNo: gridNo,
    }

    const resetColumns = resolveResetColumnDefs(gridInfo);

    if (!resetColumns || !Array.isArray(resetColumns) || resetColumns.length < 1) {
      alertMsg("초기화할 그리드 컬럼 정보가 없습니다.", "E", 3000);
      return;
    }

    setGridInitApi(getApi, params, (response) => {
      if (response.data.result) {
        skipGetGridRef.current = true;

        const cloned = cloneColumnDefs(resetColumns);

        flushSync(() => {
          if (setColumnDefs instanceof Function) {
            setColumnDefs(cloned);
          } else if (setColumnDefs?.current !== undefined) {
            setColumnDefs.current = cloned;
          }
        });

        remountGrid(cloned);

        if (showMessage) {
          alertMsg(response.data.message, "S", 3000);
        }
      } else {
        if (showMessage) {
          alertMsg(response.data.message, 'E', 3000);
        }
      }
    });
  }, [gridApi, currentPage?.seq, gridNo, getApi, showMessage, setColumnDefs, remountGrid, resolveResetColumnDefs]);

  /***************************************************************************************
   * 설명 : 그리드 설정 로딩 (페이지 진입·이동 시)
  ***************************************************************************************/
  useEffect(() => {
    const isRefresh = performance?.navigation && performance?.navigation?.type === 1;
    const isPageChange = currentPage !== prevPage?.current && Object.keys(prevPage?.current ?? []).length > 0;
    const isInitialLoad = Object.keys(prevPage?.current ?? []).length === 0;

    if (
      gridApi !== undefined &&
      Object.keys(gridApi).length > 0 &&
      currentPage &&
      currentPage?.seq &&
      (isPageChange || isRefresh || isInitialLoad)
    ) {
      getGrid(currentPage);
      prevPage.current = currentPage;
    }

  }, [gridApi, currentPage]);

  return [registerGridApi, setGridUpdate, setGridInit, gridRemountKey];
}

export default useGridConfig;
